import React, { useState, useContext } from "react";
import { getApiUrl } from "../config/api";
import { InternalLayout } from "../Components/InternalLayout";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { AuthContext } from "../contexts/AuthContext";

function ContactacoesEscala() {
  const { token } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    type: "info",
  });
  const [generatedFile, setGeneratedFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState("");

  // Função para mostrar alertas
  const showAlert = (message, type = "info") => {
    setAlert({ open: true, message, type });
    setTimeout(
      () => setAlert({ open: false, message: "", type: "info" }),
      5000,
    );
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log("🔍 Arquivo selecionado:", file.name, "Tamanho:", file.size);

    // Limpar estados anteriores ao selecionar novo arquivo
    setGeneratedFile(null);
    setUploadedFileName("");

    // Verificar se é CSV
    if (!file.name.toLowerCase().endsWith(".csv")) {
      showAlert("Por favor, selecione um arquivo CSV válido", "error");
      return;
    }

    setLoading(true);
    setUploadedFileName(file.name);

    const formData = new FormData();
    formData.append("csvFile", file);

    console.log("📤 Enviando para:", getApiUrl("/api/secretaria/contactacoes"));
    console.log("📄 FormData criado, token disponível:", !!token);

    try {
      const response = await fetch(getApiUrl("/api/secretaria/contactacoes"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      console.log(
        "📬 Resposta recebida:",
        response.status,
        response.statusText,
      );
      console.log(
        "📋 Headers da resposta:",
        Object.fromEntries(response.headers.entries()),
      );

      if (response.ok) {
        const contentType = response.headers.get("content-type");
        console.log("✅ Upload bem-sucedido, tipo de conteúdo:", contentType);

        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          console.log("📄 Dados JSON recebidos:", data);
          showAlert(data.message || "Erro ao processar arquivo", "error");
        } else {
          const blob = await response.blob();
          console.log("📦 Blob recebido, tamanho:", blob.size);
          setGeneratedFile(blob);
          showAlert(
            "Mensagens geradas com sucesso! Clique em 'Baixar Mensagens' para fazer o download.",
            "success",
          );
        }
      } else {
        const errorText = await response.text();
        console.error("❌ Erro na resposta:", response.status, errorText);
        try {
          const error = JSON.parse(errorText);
          showAlert(error.message || "Erro ao processar arquivo", "error");
        } catch {
          showAlert(`Erro ${response.status}: ${errorText}`, "error");
        }
      }
    } catch (error) {
      console.error("❌ Erro na requisição:", error);
      showAlert("Erro de conectividade: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!generatedFile) return;

    const url = URL.createObjectURL(generatedFile);
    const link = document.createElement("a");
    link.href = url;
    link.download = `mensagens_contactacao_${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showAlert(
      "Download realizado com sucesso! Estados limpos, pronto para novo upload.",
      "success",
    );

    // Limpar estados após download para permitir novos uploads
    setGeneratedFile(null);
    setUploadedFileName("");

    // Limpar o input de arquivo
    const fileInput = document.getElementById("csv-upload");
    if (fileInput) {
      fileInput.value = "";
    }

    console.log("🧹 Estados limpos após download, pronto para novo upload");
  };

  return (
    <InternalLayout>
      <Box sx={{ 
        p: { xs: 1.5, sm: 2, md: 3 }, 
        maxWidth: "1200px", 
        mx: "auto",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column"
      }}>
        {/* Alerta */}
        {alert.open && (
          <Alert
            severity={alert.type}
            onClose={() => setAlert({ open: false, message: "", type: "info" })}
            sx={{ mb: 3 }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Título */}
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2.125rem" },
            flexDirection: { xs: "column", sm: "row" },
            textAlign: { xs: "center", sm: "left" },
            fontWeight: { xs: 600, sm: 700 },
            px: { xs: 1, sm: 0 }
          }}
        >
          <AssignmentIcon sx={{ fontSize: { xs: 30, sm: 32, md: 36 } }} />
          <Box sx={{ 
            lineHeight: 1.2,
            wordBreak: "break-word",
            hyphens: "auto"
          }}>Contactações para Escala</Box>
        </Typography>

        {/* Card de Instruções */}
        <Card sx={{ 
          mb: { xs: 2, sm: 3 },
          boxShadow: { xs: 2, sm: 3 },
          borderRadius: { xs: 2, sm: 3 }
        }}>
          <CardContent sx={{ 
            p: { xs: 1.5, sm: 2, md: 3 },
            '&:last-child': { pb: { xs: 1.5, sm: 2, md: 3 } }
          }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              <InfoIcon color="primary" />
              Como usar esta funcionalidade
            </Typography>
            <List dense={isMobile}>
              <ListItem sx={{ px: { xs: 0, sm: 2 } }}>
                <ListItemIcon>
                  <CheckCircleIcon
                    fontSize={isMobile ? "small" : "medium"}
                    color="success"
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Prepare um arquivo CSV com o nome 'escala.csv'"
                  secondary="O arquivo deve conter as colunas: Data, Dia, MINISTRO, STATUS, DIRIGENTE, STATUS"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                    fontWeight: { xs: 500, sm: 400 },
                    lineHeight: { xs: 1.3, sm: 1.5 }
                  }}
                  secondaryTypographyProps={{
                    fontSize: { xs: "0.85rem", sm: "0.875rem" },
                    lineHeight: { xs: 1.3, sm: 1.43 },
                    mt: { xs: 0.5, sm: 0 }
                  }}
                />
              </ListItem>
              <ListItem sx={{ px: { xs: 0, sm: 2 } }}>
                <ListItemIcon>
                  <CheckCircleIcon
                    fontSize={isMobile ? "small" : "medium"}
                    color="success"
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Faça o upload do arquivo clicando no botão abaixo"
                  secondary="O sistema irá processar automaticamente e gerar as mensagens"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                    fontWeight: { xs: 500, sm: 400 },
                    lineHeight: { xs: 1.3, sm: 1.5 }
                  }}
                  secondaryTypographyProps={{
                    fontSize: { xs: "0.85rem", sm: "0.875rem" },
                    lineHeight: { xs: 1.3, sm: 1.43 },
                    mt: { xs: 0.5, sm: 0 }
                  }}
                />
              </ListItem>
              <ListItem sx={{ px: { xs: 0, sm: 2 } }}>
                <ListItemIcon>
                  <CheckCircleIcon
                    fontSize={isMobile ? "small" : "medium"}
                    color="success"
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Baixe o arquivo com as mensagens geradas"
                  secondary="As mensagens serão personalizadas para cada pessoa da escala"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                    fontWeight: { xs: 500, sm: 400 },
                    lineHeight: { xs: 1.3, sm: 1.5 }
                  }}
                  secondaryTypographyProps={{
                    fontSize: { xs: "0.85rem", sm: "0.875rem" },
                    lineHeight: { xs: 1.3, sm: 1.43 },
                    mt: { xs: 0.5, sm: 0 }
                  }}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Área de Upload e Download */}
        <Paper sx={{ 
          p: { xs: 1.5, sm: 2, md: 3 }, 
          textAlign: "center",
          boxShadow: { xs: 2, sm: 3 },
          borderRadius: { xs: 2, sm: 3 },
          bgcolor: { xs: "#fafafa", sm: "background.paper" }
        }}>
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <input
              accept=".csv"
              style={{ display: "none" }}
              id="csv-upload"
              type="file"
              onChange={handleFileUpload}
              disabled={loading}
            />
            <label htmlFor="csv-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <UploadIcon />
                  )
                }
                disabled={loading}
                size={isMobile ? "medium" : "large"}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: "220px" },
                  py: { xs: 2, sm: 2.5 },
                  px: { xs: 3, sm: 4 },
                  mb: { xs: 1.5, sm: 2 },
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  fontWeight: { xs: 600, sm: 500 },
                  borderRadius: { xs: 2, sm: 1.5 },
                  boxShadow: { xs: 2, sm: 3 }
                }}
              >
                {loading ? "Processando..." : "Enviar CSV da Escala"}
              </Button>
            </label>
          </Box>

          {uploadedFileName && (
            <Typography
              variant="body2"
              color="success.main"
              sx={{
                mb: { xs: 1.5, sm: 2 },
                fontSize: { xs: "0.85rem", sm: "0.9rem" },
                fontWeight: { xs: 500, sm: 400 },
                wordBreak: "break-word",
                bgcolor: { xs: "success.light", sm: "transparent" },
                color: { xs: "success.contrastText", sm: "success.main" },
                p: { xs: 1, sm: 0 },
                borderRadius: { xs: 1, sm: 0 },
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              ✓ Arquivo enviado: {uploadedFileName}
            </Typography>
          )}

          <Divider sx={{ my: { xs: 2, sm: 3 } }} />

          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={!generatedFile}
              size={isMobile ? "medium" : "large"}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "220px" },
                py: { xs: 2, sm: 2.5 },
                px: { xs: 3, sm: 4 },
                fontSize: { xs: "0.95rem", sm: "1rem" },
                fontWeight: { xs: 600, sm: 500 },
                borderRadius: { xs: 2, sm: 1.5 },
                boxShadow: { xs: 1, sm: 2 }
              }}
            >
              Baixar Mensagens
            </Button>

            {generatedFile && (
              <Button
                variant="text"
                startIcon={<UploadIcon />}
                onClick={() => {
                  setGeneratedFile(null);
                  setUploadedFileName("");
                  const fileInput = document.getElementById("csv-upload");
                  if (fileInput) fileInput.value = "";
                  showAlert("Pronto para novo upload!", "info");
                }}
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  py: { xs: 1.5, sm: 1 },
                  borderRadius: { xs: 2, sm: 1 }
                }}
              >
                Novo Processamento
              </Button>
            )}
          </Box>

          {!generatedFile && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: { xs: 2, sm: 2 },
                fontSize: { xs: "0.85rem", sm: "0.875rem" },
                px: { xs: 1, sm: 0 },
                lineHeight: { xs: 1.4, sm: 1.43 },
                maxWidth: { xs: "90%", sm: "100%" },
                mx: "auto"
              }}
            >
              📁 Faça o upload de um arquivo CSV primeiro para gerar as mensagens
            </Typography>
          )}
        </Paper>

        {/* Exemplo de formato CSV */}
        <Card sx={{ 
          mt: { xs: 2, sm: 3 },
          boxShadow: { xs: 2, sm: 3 },
          borderRadius: { xs: 2, sm: 3 }
        }}>
          <CardContent sx={{ 
            p: { xs: 1.5, sm: 2, md: 3 },
            '&:last-child': { pb: { xs: 1.5, sm: 2, md: 3 } }
          }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
            >
              Formato do arquivo CSV
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: { xs: 1, sm: 2 },
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              Exemplo do formato esperado para o arquivo escala.csv:
            </Typography>
            <Box
              component="pre"
              sx={{
                backgroundColor: { xs: "#f8f9fa", sm: "#f5f5f5" },
                padding: { xs: 1.5, sm: 2 },
                borderRadius: { xs: 2, sm: 1 },
                overflow: "auto",
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                border: "1px solid #ddd",
                lineHeight: { xs: 1.3, sm: 1.4 },
                fontFamily: "'Courier New', Courier, monospace",
                maxWidth: "100%",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                "&::-webkit-scrollbar": {
                  height: { xs: "8px", sm: "6px" },
                  width: { xs: "8px", sm: "6px" }
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                  borderRadius: "4px"
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#666"
                  }
                },
                // Melhor touch scrolling no mobile
                WebkitOverflowScrolling: "touch"
              }}
            >
              {`Data,Dia,MINISTRO,DIRIGENTE
01/03,DOM MANHÃ,Arthur Sarmento,Dayanne Sarmento
01/03,DOM NOITE,Pr. Fábio Amorim,Pr. Sérgio Pinheiro
03/03,TERÇA ,Katia Alegria,Pr. Guilerme Flor
05/03,QUINTA ESP,Sandra Lima,Dayanne Sarmento`}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </InternalLayout>
  );
}

export default ContactacoesEscala;
